import { Link } from "react-router-dom";
import useSWR from "swr";
import { ArrowUpRight, Store, Scissors, Plane, Smartphone, Check } from "lucide-react";
import { fetcher } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { Faq } from "@/components/Faq";
import { LabelModels } from "@/components/LabelModels";

const INTEL = [
  { slug: "skin-care", formats: "Serums · Creams · Face wash · Masks", actives: "Niacinamide, Retinal, Vit-C, PDRN, Peptides", channel: "D2C · Marketplaces", launch: "Highest repeat rate" },
  { slug: "hair-care", formats: "Shampoo · Conditioner · Hair oil · Serum", actives: "Redensyl, Rosemary, Biotin, Keratin", channel: "Salons · D2C", launch: "Fast volume scale" },
  { slug: "body-care", formats: "Lotions · Body wash · Scrubs · Butters", actives: "Ceramides, Shea, AHA, Squalane", channel: "Retail · Gifting", launch: "Large basket size" },
  { slug: "sun-care", formats: "Gel · Fluid · Stick · Tinted SPF", actives: "Hybrid filters, Zinc, Vit-E", channel: "D2C · Pharmacy", launch: "Year-round demand" },
  { slug: "baby-care", formats: "Wash · Lotion · Oil · Rash cream", actives: "Calendula, Oat, Panthenol", channel: "Pharmacy · Modern trade", launch: "Trust-led loyalty" },
  { slug: "mens-grooming", formats: "Beard oil · Face wash · Shave · Deo", actives: "Charcoal, Caffeine, Salicylic", channel: "D2C · Barbers", launch: "Under-served segment" },
  { slug: "lip-care", formats: "Balms · Oils · Scrubs · Tints", actives: "Shea, Jojoba, Hyaluronic", channel: "Impulse · Gifting", launch: "Low ticket entry SKU" },
];

const MODELS = [
  { icon: Smartphone, t: "D2C Founder", d: "Start with 3-5 hero SKUs in Skin Care — serum, moisturiser, sunscreen. Highest LTV and content-friendly.", pick: "Skin · Sun" },
  { icon: Scissors, t: "Salon & Clinic", d: "Professional-grade hair and skin treatments with back-bar sizes and retail take-home kits.", pick: "Hair · Skin" },
  { icon: Store, t: "Retail & Pharmacy", d: "Mass-premium Body, Baby and Lip lines with compliant labelling and shelf-ready cartons.", pick: "Body · Baby · Lip" },
  { icon: Plane, t: "Export Buyer", d: "Halal, CPNP and FDA-ready dossiers for GCC, EU, US and Africa. Multi-language labels.", pick: "All 7 verticals" },
];

const ACTIVES = ["PDRN", "Exosomes", "Encapsulated Retinal", "Niacinamide 10%", "Ceramide NP/AP/EOP", "Bio-fermented Collagen", "Peptide Complex", "Redensyl", "Hybrid SPF filters", "Squalane", "Beta-Glucan", "Rice Ferment"];

const FAQ = [
  { q: "Can I launch products from multiple categories at once?", a: "Yes. Most founders start with 3-5 SKUs, often across Skin and Body or Hair and Men's Grooming. MOQ starts from 2,000 units per SKU for the Indian market and 5,000 units per SKU for the global market." },
  { q: "Can you customise an existing formula from the catalogue?", a: "Absolutely. Fragrance, colour, active percentages and claims can be tuned. Bigger changes go through our R&D lab with stability testing." },
  { q: "How long does a full category launch take?", a: "Samples ship in 72 hours. After sample approval, production and packaging typically take 3-5 weeks depending on packaging availability." },
  { q: "Do you help with claims and labelling?", a: "Yes — our regulatory team prepares ingredient lists, claims, and CDSCO-compliant labels. Export labelling for 30+ countries is also handled in-house." },
];

export default function Categories() {
  const { data: categories } = useSWR("/categories", fetcher);

  return (
    <div className="pt-32 pb-24 max-w-[1400px] mx-auto px-6 lg:px-10">
      <div className="grid lg:grid-cols-12 gap-10 mb-16">
        <div className="lg:col-span-8">
          <div className="eyebrow mb-5">Categories</div>
          <h1 className="font-editorial text-5xl md:text-7xl text-[#1D3A2F] leading-[0.98]">
            Seven categories.
            <br />
            <em className="gold-text">
              Endless brand possibilities.
            </em>
          </h1>
        </div>
        <div className="lg:col-span-4 flex items-end">
          <p className="text-lg text-[#222222]/70 leading-relaxed">
            Every category is a launchpad. Backed by 600+ formulas, market
            insights and end-to-end launch support.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5" data-testid="categories-grid">
        {(categories || []).map((c, i) => {
          const layout = [["lg:col-span-7", "lg:h-[560px]"], ["lg:col-span-5", "lg:h-[560px]"], ["lg:col-span-4", "lg:h-[460px]"], ["lg:col-span-4 lg:mt-12", "lg:h-[460px]"], ["lg:col-span-4", "lg:h-[460px]"], ["lg:col-span-5", "lg:h-[480px]"], ["lg:col-span-7", "lg:h-[480px]"]][i] || ["lg:col-span-4", "lg:h-[460px]"];
          return (
          <Reveal key={c.slug} delay={(i % 3) * 90} className={layout[0]}>
          <Link
            to={`/categories/${c.slug}`}
            data-testid={`categories-card-${c.slug}`}
            className={`group relative block overflow-hidden rounded-[2rem] h-[420px] ${layout[1]}`}
          >
              <img
                src={c.hero_image}
                alt={c.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F251C]/95 via-[#1D3A2F]/25 to-transparent" />
              <div className="absolute inset-3 rounded-[1.6rem] border border-[#F4E4BD]/0 group-hover:border-[#F4E4BD]/60 transition-colors duration-700" />
              <div className="absolute top-7 left-7 flex items-center gap-3">
                <span className="font-editorial italic text-2xl text-[#F4E4BD]">{String(i + 1).padStart(2, "0")}</span>
                <span className="h-px w-10 bg-[#C9A961]/70 transition-[width] duration-700 group-hover:w-20" />
              </div>
              <span className="absolute top-6 right-6 w-12 h-12 rounded-full glass-card flex items-center justify-center transition-[transform,background-color] duration-500 group-hover:rotate-45 group-hover:bg-[#C9A961]">
                <ArrowUpRight className="w-5 h-5 text-[#1D3A2F]" />
              </span>
              <div className="absolute inset-x-7 bottom-7 text-[#FDFBF7]">
                <h2 className="font-editorial text-4xl md:text-5xl leading-[1.02]">{c.name}</h2>
                <div className="text-sm text-[#FDFBF7]/75 mt-2 line-clamp-1">{c.tagline}</div>
                <div className="mt-5 flex flex-wrap items-center gap-2 max-h-0 opacity-0 group-hover:max-h-20 group-hover:opacity-100 transition-[max-height,opacity] duration-700 overflow-hidden lg:flex">
                  {c.market_size && <span className="text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 rounded-full border border-[#C9A961]/70 text-[#F4E4BD]">Market {c.market_size}</span>}
                  {c.cagr && <span className="text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 rounded-full btn-gold font-semibold">CAGR {c.cagr}</span>}
                </div>
              </div>
          </Link>
          </Reveal>
          );
        })}
      </div>
      {/* ─── CATEGORY INTELLIGENCE ─── */}
      <section className="mt-32" data-testid="category-intelligence">
        <Reveal className="grid lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-5 !text-[#A68840]">Category Intelligence</div>
            <h2 className="font-editorial text-5xl lg:text-6xl text-[#1D3A2F] leading-[1.02]">
              What sells, <em className="gold-text">in every vertical.</em>
            </h2>
          </div>
          <p className="lg:col-span-4 lg:col-start-9 self-end text-[#222]/70 leading-relaxed">
            Formats, trending actives and the best route-to-market for each category — distilled from 500+ brand launches.
          </p>
        </Reveal>
        <div className="space-y-3">
          {INTEL.map((r, i) => {
            const name = (categories || []).find((c) => c.slug === r.slug)?.name || r.slug.replace(/-/g, " ");
            return (
              <Reveal key={r.slug} delay={i * 50}>
                <Link to={`/categories/${r.slug}`} data-testid={`intel-row-${r.slug}`} className="group grid md:grid-cols-12 gap-4 items-center rounded-2xl bg-[#FDFBF7] border border-[#D7D4CF]/60 p-6 gold-hover-border">
                  <div className="md:col-span-3 flex items-center gap-4">
                    <span className="font-editorial italic text-[#A68840] text-xl">{String(i + 1).padStart(2, "0")}</span>
                    <span className="font-editorial text-2xl text-[#1D3A2F] capitalize">{name}</span>
                  </div>
                  <div className="md:col-span-3 text-sm text-[#222]/75"><span className="eyebrow block mb-1">Formats</span>{r.formats}</div>
                  <div className="md:col-span-3 text-sm text-[#222]/75"><span className="eyebrow block mb-1">Hero actives</span>{r.actives}</div>
                  <div className="md:col-span-2 text-sm text-[#222]/75"><span className="eyebrow block mb-1">Best channel</span>{r.channel}</div>
                  <div className="md:col-span-1 flex md:justify-end">
                    <span className="w-10 h-10 rounded-full bg-[#F7F4EE] flex items-center justify-center transition-[transform,background-color] duration-500 group-hover:rotate-45 group-hover:bg-[#C9A961]"><ArrowUpRight className="w-4 h-4 text-[#1D3A2F]" /></span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* ─── PRIVATE vs WHITE LABEL ─── */}
      <section className="mt-32" data-testid="categories-label-models">
        <Reveal className="grid lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-5 !text-[#A68840]">Private Label vs White Label</div>
            <h2 className="font-editorial text-5xl lg:text-6xl text-[#1D3A2F] leading-[1.02]">Choose how you <em className="gold-text">want to launch.</em></h2>
          </div>
          <p className="lg:col-span-4 lg:col-start-9 self-end text-[#222]/70 leading-relaxed">
            Every category above is available in both models. Private Label gives you an exclusive, custom formula; White Label gets you to market fastest with a proven one.
          </p>
        </Reveal>
        <LabelModels />
      </section>

      {/* ─── BUSINESS MODEL ─── */}
      <section className="mt-32">
        <Reveal className="mb-12">
          <div className="eyebrow mb-5 !text-[#A68840]">Which category fits you?</div>
          <h2 className="font-editorial text-5xl lg:text-6xl text-[#1D3A2F] leading-[1.02]">Choose by <em className="gold-text">business model.</em></h2>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {MODELS.map((m, i) => {
            const Icon = m.icon;
            return (
              <Reveal key={m.t} delay={i * 80} data-testid={`model-card-${i}`} className={`group rounded-[2rem] p-8 border gold-hover-border ${i === 0 ? "bg-[#1D3A2F] text-[#FDFBF7] border-[#1D3A2F]" : "bg-[#FDFBF7] border-[#D7D4CF]/70"}`}>
                <Icon className={`w-7 h-7 mb-8 transition-transform duration-500 group-hover:-rotate-12 ${i === 0 ? "text-[#C9A961]" : "text-[#1D3A2F]"}`} strokeWidth={1.3} />
                <h3 className={`font-editorial text-3xl ${i === 0 ? "" : "text-[#1D3A2F]"}`}>{m.t}</h3>
                <p className={`text-sm mt-3 leading-relaxed ${i === 0 ? "text-[#FDFBF7]/70" : "text-[#222]/70"}`}>{m.d}</p>
                <div className="mt-6 inline-flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 rounded-full border gold-border text-[#A68840] font-semibold"><Check className="w-3 h-3" /> {m.pick}</div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* ─── TRENDING ACTIVES ─── */}
      <section className="mt-32">
        <Reveal className="rounded-[2.5rem] hero-mesh border border-[#C9A961]/40 p-10 md:p-14 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-5 !text-[#A68840]">Trending actives · 2026</div>
            <h2 className="font-editorial text-4xl lg:text-5xl text-[#1D3A2F] leading-[1.05]">Ingredients your customers <em className="gold-text">are searching for.</em></h2>
            <p className="mt-5 text-sm text-[#222]/70 leading-relaxed">Every active below is already stability-tested in our library — ready to put into your formula from day one.</p>
          </div>
          <div className="lg:col-span-7 flex flex-wrap gap-3" data-testid="trending-actives">
            {ACTIVES.map((a, i) => (
              <span key={a} className={`px-5 py-3 rounded-full text-sm font-medium transition-transform duration-500 hover:-translate-y-1 ${i % 4 === 0 ? "btn-gold" : "glass-card text-[#1D3A2F]"}`}>{a}</span>
            ))}
          </div>
        </Reveal>
      </section>

      {/* ─── HOW IT WORKS ─── */}
      <section className="mt-32">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-6">
            <div className="eyebrow mb-5">From formula to shelf</div>
            <h2 className="font-editorial text-5xl text-[#1D3A2F] leading-[1.02]">
              Four steps to a
              <br />
              <em className="gold-text">launched brand.</em>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 flex items-end">
            <p className="text-lg text-[#222222]/70 leading-relaxed">
              Whether you're pre-launch, D2C, distributor or international
              buyer — every Fresio journey follows the same premium blueprint.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            {
              n: "01",
              t: "Pick your category",
              d: "Browse 121+ live formulas across 7 verticals. Or request a bespoke R&D brief with our chemists.",
            },
            {
              n: "02",
              t: "Design your sample",
              d: "Choose plan (Standard / Premium / Luxury) · fill size · packaging container · quantity.",
            },
            {
              n: "03",
              t: "Order the sample",
              d: "Pay online via Razorpay. Sample ships in 72 hours with your branding, actives and label.",
            },
            {
              n: "04",
              t: "Scale to launch",
              d: "Approve the sample. Fresio manufactures at MOQ 2,000 (India) / 5,000 (Global), ships to 30+ countries with full export docs.",
            },
          ].map((s) => (
            <div
              key={s.n}
              className="rounded-3xl bg-[#FDFBF7] border border-[#D7D4CF]/60 p-7 hover-lift"
            >
              <div className="eyebrow mb-4">Step {s.n}</div>
              <h3 className="font-editorial text-2xl text-[#1D3A2F]">{s.t}</h3>
              <p className="text-sm text-[#222222]/70 mt-3 leading-relaxed">
                {s.d}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ─── TRUST BADGES ─── */}
      <section className="mt-24">
        <div className="rounded-3xl bg-[#1D3A2F] p-10 md:p-14 text-[#FDFBF7]">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-4">
              <div className="eyebrow text-[#FDFBF7]/60 mb-4">
                Compliance &amp; Trust
              </div>
              <h3 className="font-editorial text-4xl leading-[1.05]">
                Every category,
                <br />
                every SKU —<br />
                <em className="text-[#D7D4CF]">audit-ready.</em>
              </h3>
            </div>
            <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                "GMP",
                "ISO Certified",
                "CDSCO",
                "FDA Export",
                "CPNP EU",
                "Cruelty-Free",
                "HALAL Certified",
                "Made in India",
              ].map((c) => (
                <div
                  key={c}
                  className="rounded-2xl border border-[#FDFBF7]/15 py-5 text-center font-editorial text-lg"
                >
                  {c}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── MARKET OPPORTUNITY BAND ─── */}
      <section className="mt-24">
        <div className="grid lg:grid-cols-12 gap-10 mb-10">
          <div className="lg:col-span-6">
            <div className="eyebrow mb-4">Why this decade</div>
            <h3 className="font-editorial text-4xl md:text-5xl text-[#1D3A2F] leading-[1.05]">
              A category-defining
              <br />
              <em className="gold-text">window is open.</em>
            </h3>
          </div>
          <div className="lg:col-span-6 flex items-end">
            <p className="text-[#222222]/70 leading-relaxed">
              India will overtake Japan as the world's 4th largest beauty
              market by 2030. New founders launching between 2026-2028 will
              define the next Nykaa, Sugar or Mamaearth.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { k: "121+", v: "Live Fresio formulas" },
            { k: "7", v: "Category verticals" },
            { k: "MOQ 2,000", v: "India · 5,000 Global" },
            { k: "30+", v: "Export countries served" },
          ].map((s) => (
            <div
              key={s.v}
              className="rounded-3xl bg-[#FDFBF7] border border-[#D7D4CF]/60 p-6"
            >
              <div className="font-editorial text-4xl text-[#1D3A2F]">
                {s.k}
              </div>
              <div className="text-xs tracking-[0.16em] uppercase text-[#556B57] mt-3">
                {s.v}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section className="mt-32 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <div className="eyebrow mb-5 !text-[#A68840]">FAQ</div>
          <h2 className="font-editorial text-5xl text-[#1D3A2F] leading-[1.02]">Category <em className="gold-text">questions.</em></h2>
          <Link to="/contact#book-meeting" data-testid="categories-book-meeting" className="btn-gold mt-8 inline-flex items-center gap-2 h-12 px-6 rounded-full text-[11px] tracking-[0.2em] uppercase font-bold">Book a Meeting <ArrowUpRight className="w-4 h-4" /></Link>
        </div>
        <div className="lg:col-span-8"><Faq items={FAQ} testid="categories-faq" /></div>
      </section>
    </div>
  );
}
